import storage from "@ep/tools/storage";

/**
 * Controla o estado de bloqueio do botão de login no header.
 */
class HeaderLoginLock {
  /**
   * Inicializa estado interno e bindings.
   */
  constructor() {
    this.buttonSelector = '#header-login-button';
    this.intervalId = null;
    this.initialized = false;
    this.buttonObserver = null;
    this.handleStorage = this.handleStorage.bind(this);
  }

  /* ===== API publica ===== */
  /**
   * Inicia sincronização do lock com storage e UI.
   * @public
   * @returns {void}
   */
  init() {
    if (this.initialized) return;
    this.initialized = true;
    window.addEventListener('storage', this.handleStorage);
    this.refresh();
  }

  /**
   * Retorna segundos restantes de bloqueio.
   * @public
   * @returns {number}
   */
  getRemainingSeconds() {
    const unlockTime = Number(storage.get('unlockTime'));
    if (!unlockTime || Number.isNaN(unlockTime)) return 0;
    return Math.max(0, Math.ceil((unlockTime - Date.now()) / 1000));
  }

  /**
   * Sincroniza estado atual sem reinicializar listeners duplicados.
   * @public
   * @returns {void}
   */
  sync() {
    if (!this.initialized) {
      this.init();
      return;
    }
    this.refresh();
  }

  /* ===== Utilitarios internos ===== */
  /**
   * Garante o rótulo padrão para restauração visual do botão.
   * @private
   * @param {HTMLButtonElement} btn Botão alvo.
   * @returns {string}
   */
  ensureDefaultLabel(btn) {
    if (!btn.dataset.defaultLabel) {
      btn.dataset.defaultLabel = btn.textContent?.trim() || window?.dic?.login?.access || '';
    }
    return btn.dataset.defaultLabel;
  }

  /**
   * Formata segundos no padrão `MM:SS`.
   * @private
   * @param {number} seconds Valor em segundos.
   * @returns {string}
   */
  format(seconds) {
    const s = Math.max(0, Math.floor(seconds));
    const hh = Math.floor(s / 3600);
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    if (hh > 0) return `${String(hh).padStart(2, '0')}:${mm}:${ss}`;
    return `${mm}:${ss}`;
  }

  /**
   * Obtém o botão de login no header.
   * @private
   * @returns {HTMLButtonElement|null}
   */
  getButton() {
    return document.querySelector(this.buttonSelector);
  }

  /**
   * Handler de sincronização via evento `storage`.
   * @private
   * @param {StorageEvent} event Evento disparado pelo navegador.
   * @returns {void}
   */
  handleStorage(event) {
    if (event.key === 'unlockTime') {
      this.sync();
    }
  }

  /**
   * Atualiza UI para estado bloqueado.
   * @private
   * @param {HTMLButtonElement|null} btn Botão alvo.
   * @param {number} remaining Segundos restantes.
   * @returns {void}
   */
  renderLocked(btn, remaining) {
    if (!btn) return;
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('login-locked');
    btn.innerHTML = '';

    const label = document.createElement('span');
    label.textContent = 'Tente novamente em:';
    label.className = 'buttonText';
    label.style.display = 'block';
    label.style.whiteSpace = 'nowrap';
    label.style.fontSize = '11px';
    const time = document.createElement('span');
    time.textContent = this.format(remaining);
    time.className = 'buttonText';
    time.style.display = 'block';
    btn.append(label, time);
  }

  /**
   * Atualiza UI para estado padrão (desbloqueado).
   * @private
   * @param {HTMLButtonElement|null} btn Botão alvo.
   * @returns {void}
   */
  renderDefault(btn) {
    if (!btn) return;
    const label = this.ensureDefaultLabel(btn);
    btn.disabled = false;
    btn.setAttribute('aria-disabled', 'false');
    btn.classList.remove('login-locked');
    btn.innerHTML = '';
    const span = document.createElement('span');
    span.textContent = label;
    btn.append(span);
  }

  /**
   * Atualiza uma vez o estado do botão e retorna se lock segue ativo.
   * @private
   * @returns {boolean}
   */
  updateOnce() {
    const btn = this.getButton();
    const remaining = this.getRemainingSeconds();

    if (!btn) {
      if (remaining > 0) {
        this.startButtonObserver();
      }
      return false;
    }

    if (remaining <= 0) {
      storage.delete('unlockTime');
      this.renderDefault(btn);
      return false;
    }

    this.renderLocked(btn, remaining);
    return true;
  }

  /**
   * Recalcula estado atual e agenda atualização periódica quando necessário.
   * @private
   * @returns {void}
   */
  refresh() {
    if (this.updateOnce()) {
      if (!this.intervalId) {
        this.intervalId = setInterval(() => {
          if (!this.updateOnce()) {
            this.stop();
          }
        }, 1000);
      }
    } else {
      this.stop();
    }
  }

  /**
   * Inicia observação do DOM até o botão de login ficar disponível.
   * @private
   * @returns {void}
   */
  startButtonObserver() {
    if (this.buttonObserver || typeof MutationObserver === 'undefined') return;
    if (!document.body) return;

    this.buttonObserver = new MutationObserver(() => {
      if (this.getButton()) {
        this.stopButtonObserver();
        this.refresh();
      }
    });

    this.buttonObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Encerra timer de atualização periódica.
   * @private
   * @returns {void}
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Interrompe observação do DOM para o botão de login.
   * @private
   * @returns {void}
   */
  stopButtonObserver() {
    if (this.buttonObserver) {
      this.buttonObserver.disconnect();
      this.buttonObserver = null;
    }
  }
}

const loginLock = new HeaderLoginLock();
export default loginLock;
